'use client';
import React, { Dispatch, FC, SetStateAction, useEffect } from 'react';
import {
  DataTable,
  DataTableSelectAllChangeEvent,
  DataTableSelectionMultipleChangeEvent,
  DataTableStateEvent,
  DataTableValueArray,
} from 'primereact/datatable';
import { Column } from 'primereact/column';
import '../../../../styles/components/table.css';
// import { Loader } from 'components/common';
import { statusBody } from '../LeaveRequestComponents';
import { DemandeType } from 'features/conge/types/leaveRequestType';
import {
  DemandAccessSource,
  DemandeConge,
  DemandesConge,
} from 'features/collaborateur/types';
import moment from 'moment';
import { useSidebarManager } from 'contexts/SidebarManagerContext';
import LeaveDetails from '../LeaveDetails';
import { useTranslation } from 'react-i18next';
import UserShortCut from 'components/common/UserShortCut';
import TableSkeleton from 'components/common/TableSkeleton';
import TruncateText from 'components/common/TruncateText';

interface LeaveRequestTableProps {
  filters: DemandeType;
  setfilters: Dispatch<SetStateAction<DemandeType>>;
  demandes: DemandesConge | null;
  selectedUsers: DemandeConge[];
  setSelectedUsers: Dispatch<SetStateAction<DemandeConge[]>>;
  loading: boolean;
  refetch: () => void;
  selectAll: boolean;
  onSelectAllChange: (checked: boolean) => void;
  excludedIds: string[];
  setExcludedIds: Dispatch<SetStateAction<string[]>>;
}

const actionsBody: (
  id: number,
  refetch: () => void,
  openSidebar: <T>(
    component: React.FC<SideBarProps<T>>,
    closeOthers: boolean,
    params?: T,
  ) => void,
) => React.ReactNode = (id, refetch, openSidebar) => {
  return (
    <button
      onClick={() =>
        openSidebar(LeaveDetails, true, {
          id: id,
          refetch: refetch,
        })
      }
      className="minthr-btn minthr-btn-icon-origin minthr-btn-light-gray minthr-btn-round minthr-p-0 minthr-sidebar-open"
    >
      <img className="minthr-icon-xs" src="/icons/arrow.svg" alt="" />
    </button>
  );
};

const UserBody: React.FC<DemandeConge> = (rowData) => {
  const { t } = useTranslation();
  return (
    <div className="minthr-flex minthr-flex-items-center">
      <UserShortCut
        userId={rowData.user?.value ?? ''}
        userPath={rowData.user?.imagePath ?? ''}
        alt={`${rowData.user?.text} Profile picture`}
        className="minthr-avatar minthr-avatar-m minthr-mr-2"
      />
      <div>
        <h5>{rowData.user?.text}</h5>
        {rowData.user?.valueGroup && (
          <TruncateText
            tag="p"
            className="text-s minthr-mb-0"
            showTooltip
            textTooltip={`${t(rowData.user?.valueGroup.split('-')[0])}-${t(
              rowData.user?.valueGroup.split('-')[1],
            )}`}
          >
            {`${t(rowData.user?.valueGroup.split('-')[0])}-${t(
              rowData.user?.valueGroup.split('-')[1],
            )}` || '---'}{' '}
          </TruncateText>
        )}
      </div>
    </div>
  );
};
const LeaveRequestTable: React.FC<LeaveRequestTableProps> = ({
  filters,
  setfilters,
  demandes,
  selectedUsers,
  setSelectedUsers,
  loading,
  refetch,
  selectAll,
  onSelectAllChange,
  excludedIds,
  setExcludedIds,
}) => {
  const { openSidebar } = useSidebarManager();
  const { t } = useTranslation();
  const handlePageChange = (event: DataTableStateEvent): void => {
    setfilters((prevFilters) => ({
      ...prevFilters,
      start: event.first,
      length: event.rows,
    }));
  };

  const handleSort = (event: DataTableStateEvent): void => {
    setfilters((prevFilters) => {
      const currentOrder = prevFilters.order[0];
      const isSameColumn = currentOrder.column === event.sortField;
      const newDir =
        isSameColumn && currentOrder.dir === 'asc' ? 'desc' : 'asc';

      return {
        ...prevFilters,
        order: [
          {
            column: event.sortField,
            dir: newDir,
            custom: false,
          },
        ],
      };
    });
  };

  const handleSelectionChange = (
    e: DataTableSelectionMultipleChangeEvent<DataTableValueArray>,
  ) => {
    const nextSelection = (e.value as DemandeConge[]) || [];
    setSelectedUsers(nextSelection);

    if (selectAll) {
      const currentPage = demandes?.data || [];
      const currentPageIds = currentPage.map((d) => d.id.toString());
      const nextSelectedIds = new Set(
        nextSelection.map((d) => d.id.toString()),
      );

      const unselectedOnPage = currentPageIds.filter(
        (id) => !nextSelectedIds.has(id),
      );
      const reSelectedOnPage = currentPageIds.filter((id) =>
        nextSelectedIds.has(id),
      );

      setExcludedIds((prev) => {
        const set = new Set(prev);
        unselectedOnPage.forEach((id) => set.add(id));
        reSelectedOnPage.forEach((id) => set.delete(id));
        return Array.from(set);
      });
    } else {
      if (excludedIds.length) setExcludedIds([]);
    }
  };

  const handleHeaderCheckboxToggle = (e: DataTableSelectAllChangeEvent) => {
    onSelectAllChange(e.checked);
    if (e.checked) {
      setExcludedIds([]);
      setSelectedUsers(demandes?.data || []);
    } else {
      setExcludedIds([]);
      setSelectedUsers([]);
    }
  };

  useEffect(() => {
    if (!selectAll) return;
    const currentPage = demandes?.data || [];
    const excludedSet = new Set(excludedIds);
    const selectedOnPage = currentPage.filter(
      (d) => !excludedSet.has(d.id.toString()),
    );
    setSelectedUsers(selectedOnPage);
  }, [demandes?.data, selectAll, excludedIds]);

  const dateTemplate = (value: Date): string => {
    return value ? moment(value).format('L') : ' ';
  };

  if (loading) {
    return (
      <TableSkeleton
        rows={10}
        headers={[
          {
            selectionMode: 'multiple',
            header: '',
            headerStyle: { width: '4rem' },
            headerClassName: 'select-checkbox',
          },
          { header: t('table.user') },
          { header: t('table.type') },
          { header: t('table.demandIn') },
          { header: t('table.start') },
          { header: t('table.end') },
          { header: t('table.accessSource') },
          { header: t('table.Nbr') },
          { header: t('table.State') },
        ]}
      />
    );
  }

  return (
    <div className="minthr-table-custom-pagination">
      <DataTable
        dataKey="id"
        lazy
        rows={filters.length}
        rowsPerPageOptions={[10, 25, 50, 100]}
        totalRecords={demandes?.recordsTotal}
        first={filters.start}
        paginator
        paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink "
        currentPageReportTemplate={`${t('paginate.display')}  {first} - {last} ${t('paginate.on')} {totalRecords}`}
        value={demandes?.data}
        selectionMode="checkbox"
        selection={selectedUsers}
        onSelectionChange={handleSelectionChange}
        selectAll={selectAll}
        onSelectAllChange={handleHeaderCheckboxToggle}
        onPage={handlePageChange}
        loading={false}
        onSort={handleSort}
        className="minthr-table minthr-mobile-table dataTable no-footer"
        sortField={filters.order[0]?.column || undefined}
        sortOrder={filters.order[0]?.dir === 'asc' ? 1 : -1}
        emptyMessage={
          <div className="minthr-text-center minthr-text-gray-500">
            {t('NoData.Empty')}
          </div>
        }
        tableClassName="minthr-table minthr-mobile-table dataTable no-footer minthr-px-1"
      >
        <Column selectionMode="multiple" className="select-checkbox"></Column>

        <Column field="user" header={t('table.user')} body={UserBody}></Column>

        <Column field="type" header={t('table.type')} sortable></Column>

        <Column
          field="createdDate"
          header={t('table.demandIn')}
          body={(rowData) => dateTemplate(rowData.createdDate)}
          sortable
        ></Column>

        <Column
          field="startDate"
          header={t('table.start')}
          body={(rowData) => dateTemplate(rowData.startDate)}
          sortable
        ></Column>

        <Column
          field="endDate"
          header={t('table.end')}
          body={(rowData) => dateTemplate(rowData.endDate)}
          sortable
        ></Column>

        <Column
          field="AccessSource"
          header={t('table.accessSource', 'Source de la demande')}
          body={(rowData) =>
            rowData.accessSource != null
              ? t(`LeaveRequest.${DemandAccessSource[rowData.accessSource]}`)
              : ''
          }
          sortable
        ></Column>

        <Column
          field="DurationValue"
          header={t('table.Nbr')}
          body={(rowData) =>
            rowData.durationUnit == 0
              ? rowData.totalDays + ' ' + t('Constant.day')
              : rowData.totalDays.toFixed(2) + ' ' + t('Time_Off_Hours')
          }
          sortable
        ></Column>

        <Column
          field="statut"
          header={t('table.State')}
          body={statusBody}
          sortable
        ></Column>

        <Column
          field="action"
          body={(rowData) => actionsBody(rowData.id, refetch, openSidebar)}
        ></Column>
      </DataTable>
    </div>
  );
};

export default LeaveRequestTable;
